// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useQuery } from '@tanstack/react-query';
import { getAlarmBrief } from '../services/alarmBriefService';
import { queryKeys } from '../lib/queryKeys';
import type { Alarm } from '../types/alarm';

/**
 * EnergyFluo fault + root cause for one alarm or anomaly. LLM-backed and slow, so it is
 * keyed per site + event type + alarm and never retried: a failure just leaves `brief`
 * undefined and the screen shows its SOP-derived fallbacks. The backend caches healthy
 * briefs for 10 min; degraded ones are not cached there, so a remount refetches. Without
 * an `alarmId` the backend would pick the code's first occurrence on the site (maybe
 * another device), so the hook abstains. The bound
 * SOPs and rule fields are the same display context the page gives Ask EnergyFluo.
 */
export function useAlarmBrief(alarm?: Alarm, eventType: 'alarm' | 'anomaly' = 'alarm') {
  const siteId = alarm?.siteId;
  // alarm.id is the rule's alarm_code; alarm.siteId is AEM's site UUID.
  const alarmCode = alarm?.id;
  const enabled = !!siteId && !!alarmCode && !!alarm?.alarmId;
  const { data, isPending } = useQuery({
    queryKey: queryKeys.alarmBrief(
      eventType,
      siteId,
      alarmCode,
      alarm?.alarmId,
      alarm?.sopIds,
      alarm?.ruleTitle,
      alarm?.category,
      alarm?.priority,
      alarm?.controlLayer
    ),
    enabled,
    queryFn: () =>
      getAlarmBrief({
        site_id: siteId as string,
        alarm_code: alarmCode as string,
        alarm_id: alarm?.alarmId ?? null,
        event_type: eventType,
        bound_sops: alarm?.sopIds ?? [],
        rule_title: alarm?.ruleTitle,
        category: alarm?.category,
        priority: alarm?.priority,
        control_layer: alarm?.controlLayer,
      }),
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
  // A disabled query stays pending forever; only report loading when it can run.
  return { brief: data, loading: enabled && isPending };
}
